import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './ViewAllClinicalSitesModal.module.scss';
import Modal from '../../../components/modal/Modal';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import LiveSearch from '../../../components/liveSearch/LiveSearch';
import axiosClient from '../../../api/axiosClient';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import ProgramClinicalSitesTable from './ProgramClinicalSitesTable';
import { useAuth } from '../../../contexts/AuthContext';

const ViewAllClinicalSitesModal = ({ isOpen, onClose, programId, programTitle, onAddSitesClick }) => {
    const [allSites, setAllSites] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const { role, can } = useAuth();
    const canAddClinicalSites = role !== 'Program Coordinator' || can('canAddClinicalSites');

    useEffect(() => {
        if (isOpen) {
            setSearchQuery('');
        }
    }, [isOpen]);

    useEffect(() => {
        const fetchSites = async () => {
            if (!isOpen || !programId) return;
            
            setIsLoading(true);
            try {
                const response = await axiosClient.get(`/programs/${programId}/clinical-sites`);
                
                let fetchedSites = response.data?.data || response.data?.clinicalSites || response.data;
                fetchedSites = Array.isArray(fetchedSites) ? fetchedSites : [];
                
                setAllSites(fetchedSites);
            } catch (error) {
                console.error("Failed to fetch clinical sites", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSites();
    }, [isOpen, programId]);

    const getFilteredSites = () => {
        if (!searchQuery) return allSites;
        const query = searchQuery.toLowerCase();
        return allSites.filter(site => 
            site.name?.toLowerCase().includes(query)
        );
    };

    if (!isOpen) return null;

    return (
        <Modal scrollable={true}>
            {isLoading && <LoadingOverlay />}
            <div className={styles.modalContent}>
                <div className={styles.header}>
                    <div>
                        <h2 className={styles.title}>All Clinical Sites</h2>
                        <span className={styles.subtitle}>{programTitle}</span>
                    </div>
                   
                    <div className={styles.actions}>
                        <LiveSearch 
                            placeholder="Search clinical sites" 
                            onChange={(val) => setSearchQuery(val)} 
                        />
                        {canAddClinicalSites && (
                            <Button
                                label={
                                    <div className={styles.cta}>
                                        <Icon name="plus" />
                                        Add Clinical Site
                                    </div>
                                }
                                variant="primary-light"
                                onClick={onAddSitesClick}
                                size='medium'
                            />
                        )}
                         <button className={styles.closeBtn} onClick={onClose}>
                            <Icon name="plus" className={styles.crossIcon} />
                        </button>
                    </div>
                </div>

                <div className={styles.tableWrapper}>
                    <ProgramClinicalSitesTable clinicalSites={getFilteredSites()} />
                </div>
            </div>
        </Modal>
    );
};

ViewAllClinicalSitesModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  programTitle: PropTypes.string,
  onAddSitesClick: PropTypes.func
};

export default ViewAllClinicalSitesModal;
